import * as THREE from 'three';
import { AU } from '../config';
import { daysSinceJ2000 } from '../astro/ephemeris';
import { createSeededRandom } from '../utils/random';

const COUNT = 20_000;

/**
 * The main belt seen from afar: 20k points on Kepler orbits (2.1–3.3 AU), positions solved on the CPU
 * a few times per second. Close up, the Asteroids sector fields take over.
 */
export class AsteroidBelt {
  readonly points: THREE.Points;
  private readonly el: Float32Array; // a, e, i, node, peri, M0, n (rad/day)
  private readonly helio: Float64Array;
  private readonly render: Float32Array;
  private lastSolve = -Infinity;

  constructor() {
    const rng = createSeededRandom(777);
    this.el = new Float32Array(COUNT * 7);
    this.helio = new Float64Array(COUNT * 3);
    this.render = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);
    for (let i = 0; i < COUNT; i++) {
      // Kirkwood-ish distribution: denser near 2.7 AU with gaps at 2.5 and 2.82.
      let a = 2.1 + rng() * 1.2;
      if (Math.abs(a - 2.5) < 0.03 || Math.abs(a - 2.82) < 0.02) a += 0.06;
      const e = Math.pow(rng(), 2) * 0.22;
      const inc = Math.pow(rng(), 2) * 0.3;
      const k = i * 7;
      this.el[k] = a;
      this.el[k + 1] = e;
      this.el[k + 2] = inc;
      this.el[k + 3] = rng() * Math.PI * 2;
      this.el[k + 4] = rng() * Math.PI * 2;
      this.el[k + 5] = rng() * Math.PI * 2;
      this.el[k + 6] = (0.9856076686 * Math.PI) / 180 / Math.pow(a, 1.5);
      const g = 0.45 + rng() * 0.35;
      colors.set([g * 1.05, g * 0.95, g * 0.85], i * 3);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.render, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.ShaderMaterial({
      uniforms: { opacity: { value: 0.22 } },
      vertexShader: /* glsl */ `
        #include <common>
        #include <logdepthbuf_pars_vertex>
        attribute vec3 color;
        varying vec3 vColor;
        varying float vFade;
        void main() {
          vColor = color;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          float d = length(mv.xyz);
          vFade = smoothstep(2e7, 2e8, d); // hide near points (sector rocks take over)
          gl_Position = projectionMatrix * mv;
          gl_PointSize = 1.2;
          #include <logdepthbuf_vertex>
        }`,
      fragmentShader: /* glsl */ `
        #include <logdepthbuf_pars_fragment>
        uniform float opacity;
        varying vec3 vColor;
        varying float vFade;
        void main() {
          #include <logdepthbuf_fragment>
          gl_FragColor = vec4(vColor * opacity * vFade, 1.0);
        }`,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    });
    this.points = new THREE.Points(geo, mat);
    this.points.frustumCulled = false;
  }

  update(timeMs: number, origin: THREE.Vector3): void {
    const d = daysSinceJ2000(timeMs);
    if (Math.abs(d - this.lastSolve) > 0.02) {
      this.lastSolve = d;
      for (let i = 0; i < COUNT; i++) {
        const k = i * 7;
        const a = this.el[k] * AU, e = this.el[k + 1], inc = this.el[k + 2], node = this.el[k + 3], peri = this.el[k + 4];
        const M = this.el[k + 5] + this.el[k + 6] * d;
        let E = M;
        for (let j = 0; j < 4; j++) E = E - (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
        const xp = a * (Math.cos(E) - e), yp = a * Math.sqrt(1 - e * e) * Math.sin(E);
        const cw = Math.cos(peri), sw = Math.sin(peri), cO = Math.cos(node), sO = Math.sin(node), cI = Math.cos(inc), sI = Math.sin(inc);
        this.helio[i * 3] = (cw * cO - sw * sO * cI) * xp + (-sw * cO - cw * sO * cI) * yp;
        this.helio[i * 3 + 1] = (cw * sO + sw * cO * cI) * xp + (-sw * sO + cw * cO * cI) * yp;
        this.helio[i * 3 + 2] = sw * sI * xp + cw * sI * yp;
      }
    }
    for (let i = 0; i < COUNT * 3; i += 3) {
      this.render[i] = this.helio[i] - origin.x;
      this.render[i + 1] = this.helio[i + 1] - origin.y;
      this.render[i + 2] = this.helio[i + 2] - origin.z;
    }
    this.points.geometry.attributes.position.needsUpdate = true;
  }
}
